import type { FilterOptionsState } from "@mui/material/useAutocomplete";
import axios from "axios";
import type { MutableRefObject, ReactNode, SyntheticEvent } from "react";
import { useCallback, useMemo, useRef, useState } from "react";

import type { VariableDataType } from "@/components/rule-canvas-builder/hooks/useRuleVariables";
import apiRoutes from "@/constants/api-routes";
import { iconKeyForVariableDataType } from "@/constants/rule-variables";
import useAuthHeader from "@/hooks/useAuthHeader";

import type { Option } from "../../constants";
import { DataTypeSelectedPill } from "../DataTypeSelectedPill";
import { formatScreenFieldDisplayLabel } from "../../utils/resource-insert-label-utils";
import { formatLiteralEdgeWhitespace } from "../../utils/format-literal-edge-whitespace";
import {
	isCrossLocationScreenField,
} from "../../utils/data-field-type-utils";
import {
	formatCollectionCurrentValueLabel,
	formatCollectionValueFromLabel,
} from "../../utils/collection-utils";
import { buildMainOptions, dedupeOptionsByValue, entityOptionKey } from "./build-main-options";
import { ALL_GROUPS } from "./constants";
import { collectionNavIcon, groupIcon, tableFieldCategory } from "./group-utils";
import type {
	ActiveScreenLocationInfo,
	ActiveTableInfo,
	DatabaseTableOptionV2,
	EntityOption,
	FormEntityListboxContextValue,
	FormEntitySelectProps,
	ResourceCategoryV2,
	ScreenLocationFieldOption,
	VisibleGroupKey,
} from "./types";

export interface UseFormEntitySelectResult {
	open: boolean;
	mainOptions: EntityOption[];
	inputValue: string;
	hasPill: boolean;
	freeInput: string;
	isValueField: boolean;
	value: string;
	filterOptions: (
		opts: (string | EntityOption)[],
		state: FilterOptionsState<string | EntityOption>,
	) => (string | EntityOption)[];
	getOptionLabel: (option: EntityOption | string) => string;
	getOptionKey: (option: EntityOption | string) => string;
	isOptionEqualToValue: (option: EntityOption | string, val: EntityOption | string) => boolean;
	handleOpen: () => void;
	handleClose: (e: SyntheticEvent, reason: string) => void;
	handleInputChange: (e: SyntheticEvent, newInput: string, reason: string) => void;
	handleChange: (e: SyntheticEvent, newValue: EntityOption | string | null) => void;
	handleInputBlur: () => void;
	renderPill: () => ReactNode;
	listboxContextValue: FormEntityListboxContextValue;
	suppressNextCloseRef: MutableRefObject<boolean>;
	activeTable: ActiveTableInfo | null;
	activeScreenLocation: ActiveScreenLocationInfo | null;
	toggleGroupExpand: (key: VisibleGroupKey) => void;
	handleTableClick: (table: DatabaseTableOptionV2, groupKey: VisibleGroupKey) => void;
	handleScreenLocationClick: (
		location: ScreenLocationFieldOption,
		groupKey: VisibleGroupKey,
	) => Promise<void>;
	drillDownOpen: boolean;
}

export function useFormEntitySelect(props: FormEntitySelectProps): UseFormEntitySelectResult {
	const {
		value,
		valueCategory,
		onChange,
		maxVisibleItems = 3,
		isValueField = false,
		visibleGroups,
		screenFields = [],
		advancedTriggers = [],
		collections = [],
		currentCollectionOptions = [],
		otherCollectionOptions = [],
		collectionCurrentValueCollectionId,
		variableGroups = [],
		formulaVariables = [],
		databaseSingleRowVariableOptions = [],
		singleRowDbVariables = [],
		multiRowDbVariables = [],
		screenTables = [],
		globalConstants = [],
		reactionResources = [],
		otherScreenLocations = [],
		suggestedScreenLocations = [],
		selectedFieldMeta,
		ruleScreenLocationName,
		crossLocationPillEnhancements = false,
		visualizeEdgeWhitespace = false,
	} = props;

	const authHeader = useAuthHeader();
	const [open, setOpen] = useState(false);
	const [inputValue, setInputValue] = useState("");
	const [activeTable, setActiveTable] = useState<ActiveTableInfo | null>(null);
	const [activeScreenLocation, setActiveScreenLocation] = useState<ActiveScreenLocationInfo | null>(null);
	const [locationFieldsCache, setLocationFieldsCache] = useState<Record<string, Option[]>>({});
	const [subPanelLoading, setSubPanelLoading] = useState(false);
	const [subPanelError, setSubPanelError] = useState(false);
	const [expandedGroups, setExpandedGroups] = useState<Set<VisibleGroupKey>>(
		() => new Set<VisibleGroupKey>(),
	);
	const [freeInput, setFreeInput] = useState("");
	/** Prevents Autocomplete from closing when drilling into a table or expanding a group (MUI still fires onClose). */
	const suppressNextCloseRef = useRef(false);
	/**
	 * After a sub-panel field pick, MUI may still fire onChange with the main list option at the same
	 * flattened index — ignore that spurious field commit.
	 */
	const ignoreNextAutocompleteFieldChangeRef = useRef(false);

	const effectiveGroups = visibleGroups ?? ALL_GROUPS;

	const dedupedGlobalConstants = useMemo(() => {
		if (!isValueField) return globalConstants;
		return dedupeOptionsByValue(globalConstants);
	}, [isValueField, globalConstants]);

	const currentScreenFieldIds = useMemo(
		() => new Set(screenFields.map((f) => f.value)),
		[screenFields],
	);

	const screenLocationNameById = useMemo(() => {
		const map = new Map<string, string>();
		for (const loc of [...otherScreenLocations, ...suggestedScreenLocations]) {
			map.set(String(loc.id), loc.name);
		}
		return map;
	}, [otherScreenLocations, suggestedScreenLocations]);

	const formatScreenFieldLabel = useCallback(
		(label: string, fieldId?: string, locationName?: string) =>
			formatScreenFieldDisplayLabel(label, fieldId, currentScreenFieldIds, {
				ruleLocationName: ruleScreenLocationName,
				locationName,
			}),
		[currentScreenFieldIds, ruleScreenLocationName],
	);

	const selectedMeta = useMemo((): {
		label: string;
		dataType?: string;
		isCrossLocation?: boolean;
		fieldName?: string;
	} | null => {
		if (!value) return null;

		if (valueCategory === "globalConstant") {
			for (const opt of dedupedGlobalConstants) {
				if (opt.value === value) return { label: opt.label, dataType: opt.dataType };
			}
			return { label: value };
		}

		if (valueCategory === "screenField") {
			for (const f of screenFields) {
				if (f.value === value || f.label === value) {
					return {
						label: formatScreenFieldLabel(f.label, f.value),
						dataType: f.dataType,
						isCrossLocation: false,
					};
				}
			}
			for (const [locId, fields] of Object.entries(locationFieldsCache)) {
				for (const f of fields) {
					if (f.value === value || f.label === value) {
						const locationName = screenLocationNameById.get(locId);
						return {
							label: formatScreenFieldLabel(f.label, f.value, locationName),
							dataType: f.dataType,
							fieldName: f.label,
							isCrossLocation: isCrossLocationScreenField(
								f.value,
								locationName
									? { name: f.label, location: { name: locationName } }
									: { name: f.label },
								currentScreenFieldIds,
								ruleScreenLocationName,
							),
						};
					}
				}
			}
			if (selectedFieldMeta?.label || selectedFieldMeta?.dataType) {
				const isCrossLocation =
					selectedFieldMeta.isCrossLocation ??
					isCrossLocationScreenField(
						value,
						selectedFieldMeta.fieldName
							? {
									name: selectedFieldMeta.fieldName,
									...(selectedFieldMeta.locationName
										? { location: { name: selectedFieldMeta.locationName } }
										: {}),
								}
							: undefined,
						currentScreenFieldIds,
						ruleScreenLocationName,
					);
				return {
					label: selectedFieldMeta.label
						? formatScreenFieldLabel(
								selectedFieldMeta.label,
								value,
								selectedFieldMeta.locationName,
							)
						: formatScreenFieldLabel(value, value, selectedFieldMeta.locationName),
					dataType: selectedFieldMeta.dataType,
					fieldName: selectedFieldMeta.fieldName,
					isCrossLocation,
				};
			}
			return {
				label: formatScreenFieldLabel(value, value),
				isCrossLocation: isCrossLocationScreenField(
					value,
					undefined,
					currentScreenFieldIds,
					ruleScreenLocationName,
				),
			};
		}

		if (valueCategory === "advancedTrigger") {
			for (const t of advancedTriggers) {
				if (t.value === value || t.label === value) {
					return { label: t.label, dataType: t.dataType };
				}
			}
			return { label: value };
		}

		if (valueCategory === "collection") {
			const allCollectionOpts = [
				...collections,
				...currentCollectionOptions,
				...otherCollectionOptions,
			];
			for (const c of allCollectionOpts) {
				if (c.value === value || c.label === value) {
					const showCurrentValue =
						!isValueField &&
						collectionCurrentValueCollectionId != null &&
						String(c.value) === String(collectionCurrentValueCollectionId);
					const baseLabel = showCurrentValue
						? formatCollectionCurrentValueLabel(c.label)
						: isValueField
							? formatCollectionValueFromLabel(c.label)
							: c.label;
					return {
						label: baseLabel,
						dataType: c.dataType,
					};
				}
			}
			return { label: value };
		}

		if (valueCategory === "simpleVariable") {
			for (const group of variableGroups) {
				for (const opt of group.options) {
					if (opt.value === value) return { label: opt.label, dataType: opt.dataType };
				}
			}
		}

		if (valueCategory === "formulaVariable") {
			for (const opt of formulaVariables) {
				if (opt.value === value) return { label: opt.label, dataType: opt.dataType };
			}
			if (selectedFieldMeta?.label || selectedFieldMeta?.dataType) {
				return {
					label: selectedFieldMeta.label ?? value,
					dataType: selectedFieldMeta.dataType,
				};
			}
		}

		if (valueCategory === "singleRowDbVariableField" || valueCategory === "multiRowDbVariableField" || valueCategory === "screenTableField") {
			for (const tableSets of [singleRowDbVariables, multiRowDbVariables, screenTables]) {
				for (const table of tableSets) {
					for (const field of table.fields) {
						if (field.value === value) return { label: field.label, dataType: field.dataType };
					}
				}
			}
		}

		if (valueCategory === "reactionResource") {
			for (const opt of reactionResources) {
				if (opt.value === value || opt.label === value) return { label: opt.label, dataType: opt.dataType };
			}
		}

		// Free-typed literal (no structured entity category).
		return { label: value };
	}, [
		value,
		valueCategory,
		screenFields,
		advancedTriggers,
		collections,
		currentCollectionOptions,
		otherCollectionOptions,
		collectionCurrentValueCollectionId,
		isValueField,
		variableGroups,
		formulaVariables,
		databaseSingleRowVariableOptions,
		singleRowDbVariables,
		multiRowDbVariables,
		screenTables,
		dedupedGlobalConstants,
		reactionResources,
		locationFieldsCache,
		formatScreenFieldLabel,
		selectedFieldMeta,
		ruleScreenLocationName,
		currentScreenFieldIds,
		screenLocationNameById,
	]);

	const drillDownOpen = Boolean(activeTable || activeScreenLocation);

	const subPanelTitle = activeTable
		? activeTable.table.label
		: activeScreenLocation
			? activeScreenLocation.location.name
			: "";

	const qLower = inputValue.toLowerCase().trim();
	/** While drill-down open, typing filters sub-fields only — keep the main list stable. */
	const queryForMainList = drillDownOpen ? "" : qLower;

	const mainOptions = useMemo(
		() =>
			buildMainOptions(
				effectiveGroups,
				queryForMainList,
				expandedGroups,
				maxVisibleItems,
				screenFields,
				advancedTriggers,
				collections,
				currentCollectionOptions,
				otherCollectionOptions,
				variableGroups,
				formulaVariables,
				databaseSingleRowVariableOptions,
				singleRowDbVariables,
				multiRowDbVariables,
				screenTables,
				dedupedGlobalConstants,
				reactionResources,
				otherScreenLocations,
				suggestedScreenLocations,
				locationFieldsCache,
			),
		[
			effectiveGroups,
			queryForMainList,
			expandedGroups,
			maxVisibleItems,
			screenFields,
			advancedTriggers,
			collections,
			currentCollectionOptions,
			otherCollectionOptions,
			variableGroups,
			formulaVariables,
			databaseSingleRowVariableOptions,
			singleRowDbVariables,
			multiRowDbVariables,
			screenTables,
			dedupedGlobalConstants,
			reactionResources,
			otherScreenLocations,
			suggestedScreenLocations,
			locationFieldsCache,
		],
	);

	const filteredSubFields = useMemo((): Option[] => {
		const q = inputValue.toLowerCase().trim();
		if (activeTable) {
			const fields = activeTable.table.fields;
			return q ? fields.filter((f) => f.label.toLowerCase().includes(q)) : fields;
		}
		if (activeScreenLocation) {
			const locId = String(activeScreenLocation.location.id);
			const fields =
				locationFieldsCache[locId] ?? activeScreenLocation.location.fields ?? [];
			return q ? fields.filter((f) => f.label.toLowerCase().includes(q)) : fields;
		}
		return [];
	}, [activeTable, activeScreenLocation, inputValue, locationFieldsCache]);

	const fieldCategoryForSubPanel = activeTable
		? activeTable.category
		: activeScreenLocation
			? activeScreenLocation.category
			: "simpleVariable";

	const commitAndReset = useCallback(() => {
		setOpen(false);
		setInputValue("");
		setActiveTable(null);
		setActiveScreenLocation(null);
		setSubPanelLoading(false);
		setSubPanelError(false);
		setExpandedGroups(new Set<VisibleGroupKey>());
	}, []);

	const handleSelect = useCallback(
		(
			selectedValue: string,
			category: ResourceCategoryV2,
			selectedLabel: string,
			dataType?: string,
			locationName?: string,
		) => {
			onChange(selectedValue, category, selectedLabel, dataType, locationName);
			setFreeInput("");
			commitAndReset();
		},
		[onChange, commitAndReset],
	);

	const handleFieldSelect = useCallback(
		(opt: Option, category: ResourceCategoryV2) => {
			ignoreNextAutocompleteFieldChangeRef.current = true;
			const locationName = activeScreenLocation?.location.name;
			handleSelect(opt.value, category, opt.label, opt.dataType, locationName);
			window.setTimeout(() => {
				ignoreNextAutocompleteFieldChangeRef.current = false;
			}, 300);
		},
		[handleSelect, activeScreenLocation],
	);

	const handleBack = useCallback(() => {
		setActiveTable(null);
		setActiveScreenLocation(null);
		setSubPanelLoading(false);
		setSubPanelError(false);
	}, []);

	const mapScreenFieldsFromApi = useCallback(
		(_locationName: string, raw: unknown, _groupKey: VisibleGroupKey): Option[] => {
			if (!Array.isArray(raw)) return [];
			return raw.map((field: { id: number; name: string; dataFieldType?: { name: string } }) => ({
				label: field.name,
				value: String(field.id),
				dataType: field.dataFieldType?.name,
			}));
		},
		[],
	);

	const handleScreenLocationClick = useCallback(
		async (location: ScreenLocationFieldOption, groupKey: VisibleGroupKey) => {
			setActiveTable(null);
			setSubPanelError(false);
			const locId = String(location.id);
			const cached = locationFieldsCache[locId];
			setActiveScreenLocation({
				location: { ...location, fields: cached ?? location.fields },
				category: "screenField",
				groupKey,
			});

			if (cached) return;

			setSubPanelLoading(true);
			try {
				const res = await axios.get(apiRoutes.getScreenFields(location.id), {
					headers: authHeader,
				});
				const fields = mapScreenFieldsFromApi(location.name, res.data, groupKey);
				setLocationFieldsCache((prev) => ({ ...prev, [locId]: fields }));
				setActiveScreenLocation((prev) =>
					prev && String(prev.location.id) === locId
						? { ...prev, location: { ...prev.location, fields } }
						: prev,
				);
			} catch {
				setSubPanelError(true);
				setActiveScreenLocation((prev) =>
					prev && String(prev.location.id) === locId
						? { ...prev, location: { ...prev.location, fields: [] } }
						: prev,
				);
			} finally {
				setSubPanelLoading(false);
			}
		},
		[authHeader, locationFieldsCache, mapScreenFieldsFromApi],
	);

	/** Must be a no-arg callback — DataTypeSelectedPill calls `onClear()` without an event. */
	const handleClear = useCallback(() => {
		onChange("", "literal", "");
		setFreeInput("");
		setInputValue("");
		commitAndReset();
	}, [onChange, commitAndReset]);

	const toggleGroupExpand = useCallback((key: VisibleGroupKey) => {
		setExpandedGroups((prev) => {
			const next = new Set<VisibleGroupKey>(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	}, []);

	const handleTableClick = useCallback((table: DatabaseTableOptionV2, groupKey: VisibleGroupKey) => {
		setActiveScreenLocation(null);
		setSubPanelLoading(false);
		setSubPanelError(false);
		setActiveTable({ table, category: tableFieldCategory(groupKey) });
	}, []);

	const handleAutocompleteClose = useCallback(
		(_event: SyntheticEvent, reason: string) => {
			if (
				isValueField &&
				freeInput.length > 0 &&
				reason !== "selectOption" &&
				reason !== "toggleInput"
			) {
				onChange(freeInput, "literal", freeInput);
			}
			setFreeInput("");
			setActiveTable(null);
			setActiveScreenLocation(null);
			setSubPanelLoading(false);
			setSubPanelError(false);
			setExpandedGroups(new Set<VisibleGroupKey>());
			setInputValue("");
		},
		[isValueField, freeInput, onChange],
	);

	const listboxContextValue = useMemo(
		(): FormEntityListboxContextValue => ({
			activeTable,
			activeScreenLocation,
			drillDownOpen,
			subPanelTitle,
			filteredSubFields,
			fieldCategory: fieldCategoryForSubPanel,
			subPanelLoading,
			subPanelError,
			searchQuery: inputValue,
			selectedValue: value,
			onBack: handleBack,
			onFieldSelect: handleFieldSelect,
		}),
		[
			activeTable,
			activeScreenLocation,
			drillDownOpen,
			subPanelTitle,
			filteredSubFields,
			fieldCategoryForSubPanel,
			subPanelLoading,
			subPanelError,
			inputValue,
			value,
			handleBack,
			handleFieldSelect,
		],
	);

	const hasPill = Boolean(value && selectedMeta && !freeInput);

	const renderPill = () => {
		if (!hasPill || !selectedMeta) return null;
		const literalLabel =
			visualizeEdgeWhitespace && valueCategory === "literal"
				? formatLiteralEdgeWhitespace(selectedMeta.label)
				: selectedMeta.label;
		const literalTooltip =
			visualizeEdgeWhitespace && valueCategory === "literal" ? value : undefined;
		if (valueCategory === "globalConstant") {
			return (
				<DataTypeSelectedPill
					leadingIcon={groupIcon("globalConstants")}
					label={literalLabel}
					onClear={handleClear}
					clearAriaLabel="Clear value"
					tooltipTitle={literalTooltip}
				/>
			);
		}
		if (valueCategory === "collection") {
			return (
				<DataTypeSelectedPill
					leadingIcon={collectionNavIcon()}
					label={literalLabel}
					onClear={handleClear}
					clearAriaLabel={isValueField ? "Clear value" : "Clear resource"}
					tooltipTitle={literalTooltip}
				/>
			);
		}
		const iconKey = selectedMeta.dataType
			? iconKeyForVariableDataType(selectedMeta.dataType as VariableDataType)
			: "text";
		const showCrossLocationPill =
			crossLocationPillEnhancements &&
			valueCategory === "screenField" &&
			Boolean(selectedMeta.isCrossLocation);

		return (
			<DataTypeSelectedPill
				iconKey={iconKey}
				label={literalLabel}
				onClear={handleClear}
				clearAriaLabel="Clear resource"
				truncateStart={showCrossLocationPill}
				truncateFieldName={selectedMeta.fieldName}
				tooltipTitle={literalTooltip}
			/>
		);
	};

	const filterOptions = useCallback(
		(
			opts: (string | EntityOption)[],
			state: FilterOptionsState<string | EntityOption>,
		): (string | EntityOption)[] => {
			if (drillDownOpen) return opts;
			const q = state.inputValue.toLowerCase().trim();
			if (!q) return opts;
			return opts.filter((opt) => {
				if (typeof opt === "string") return false;
				if (opt.kind === "showMore") return true;
				if (opt.kind === "field") return opt.option.label.toLowerCase().includes(q);
				if (opt.kind === "table") {
					return (
						opt.table.label.toLowerCase().includes(q) ||
						opt.table.fields.some((f) => f.label.toLowerCase().includes(q))
					);
				}
				if (opt.kind === "screenLocation") {
					if (opt.location.name.toLowerCase().includes(q)) return true;
					const cached = locationFieldsCache[String(opt.location.id)] ?? opt.location.fields ?? [];
					return cached.some((f) => f.label.toLowerCase().includes(q));
				}
				return false;
			});
		},
		[drillDownOpen, locationFieldsCache],
	);

	const getOptionLabel = useCallback((option: EntityOption | string) => {
		if (typeof option === "string") return option;
		if (option.kind === "field") return option.option.label;
		if (option.kind === "table") return option.table.label;
		if (option.kind === "screenLocation") return option.location.name;
		if (option.kind === "showMore") return "";
		return "";
	}, []);

	const getOptionKey = useCallback(
		(option: EntityOption | string) => entityOptionKey(option),
		[],
	);

	const isOptionEqualToValue = useCallback(
		(option: EntityOption | string, val: EntityOption | string) => {
			if (typeof option === "string" || typeof val === "string") {
				return option === val;
			}
			if (option.kind !== "field" || val.kind !== "field") return false;
			return option.option.value === val.option.value;
		},
		[],
	);

	const handleOpen = useCallback(() => {
		setOpen(true);
		setInputValue(isValueField ? freeInput : "");
		setActiveTable(null);
		setActiveScreenLocation(null);
		setSubPanelLoading(false);
		setSubPanelError(false);
		setExpandedGroups(new Set<VisibleGroupKey>());
	}, [isValueField, freeInput]);

	const handleClose = useCallback(
		(e: SyntheticEvent, reason: string) => {
			if (suppressNextCloseRef.current) {
				suppressNextCloseRef.current = false;
				setOpen(true);
				return;
			}
			setOpen(false);
			handleAutocompleteClose(e, reason);
		},
		[handleAutocompleteClose],
	);

	const handleInputChange = useCallback(
		(_e: SyntheticEvent, newInput: string, reason: string) => {
			if (reason === "reset") return;
			if (reason === "clear") {
				setInputValue("");
				if (isValueField) setFreeInput("");
				return;
			}
			setInputValue(newInput);
			if (isValueField) {
				setFreeInput(newInput);
				if (value) onChange("", "literal", "");
			}
		},
		[isValueField, value, onChange],
	);

	const handleChange = useCallback(
		(_e: SyntheticEvent, newValue: EntityOption | string | null) => {
			if (newValue === null) {
				onChange("", "literal", "");
				setFreeInput("");
				setInputValue("");
				return;
			}
			if (typeof newValue === "string") {
				if (newValue.length > 0) onChange(newValue, "literal", newValue);
				setFreeInput("");
				commitAndReset();
				return;
			}
			if (newValue.kind === "showMore") {
				toggleGroupExpand(newValue.groupKey);
				return;
			}
			if (newValue.kind === "table") {
				handleTableClick(newValue.table, newValue.groupKey);
				return;
			}
			if (newValue.kind === "screenLocation") {
				void handleScreenLocationClick(newValue.location, newValue.groupKey);
				return;
			}
			if (newValue.kind === "field") {
				// Sub-panel selection is handled only via handleFieldSelect; ignore duplicate /
				// mis-indexed commits from Autocomplete while the drill-down is active or until the popup closes.
				if (drillDownOpen) {
					return;
				}
				if (ignoreNextAutocompleteFieldChangeRef.current) {
					return;
				}
				handleSelect(
					newValue.option.value,
					newValue.category,
					newValue.option.label,
					newValue.option.dataType,
				);
			}
		},
		[
			onChange,
			commitAndReset,
			toggleGroupExpand,
			handleTableClick,
			handleScreenLocationClick,
			drillDownOpen,
			handleSelect,
		],
	);

	const handleInputBlur = useCallback(() => {
		if (isValueField && !open && freeInput.length > 0) {
			onChange(freeInput, "literal", freeInput);
			setFreeInput("");
			setInputValue("");
		}
	}, [isValueField, open, freeInput, onChange]);

	return {
		open,
		mainOptions,
		inputValue,
		hasPill,
		freeInput,
		isValueField,
		value,
		filterOptions,
		getOptionLabel,
		getOptionKey,
		isOptionEqualToValue,
		handleOpen,
		handleClose,
		handleInputChange,
		handleChange,
		handleInputBlur,
		renderPill,
		listboxContextValue,
		suppressNextCloseRef,
		activeTable,
		activeScreenLocation,
		toggleGroupExpand,
		handleTableClick,
		handleScreenLocationClick,
		drillDownOpen,
	};
}
